<template>
  <div>
    <b-card class="card-custom" header-tag="header" footer-tag="footer">
      <template v-slot:header>
        <div class="card-title">
          <h3 class="card-label">
            {{ $t("STOCK_REFERENCE_SUMMARY_DOC") }}
            <i class="mr-2"></i>
            <small class="">{{ $t("LIST") }}</small>
          </h3>
        </div>
        <div class="card-toolbar"></div>
      </template>
      <v-card v-show="!isPageLoading">
        <SearchTools
          :searchQuery.sync="searchQuery"
          :searchableText="false"
          :clearable="false"
          @do-search="doSearch"
          @do-clear="doClear"
        >
          <template v-slot:prepend>
            <b-col :sm="6">
              <FormDatepickerBase
                :type="'text'"
                :clearable="true"
                :max="maxDate"
                :i18n="{
                  label: 'DATE',
                  placeholder: $t('DATE')
                }"
                :date.sync="filters.date"
              ></FormDatepickerBase>
            </b-col>

            <b-col :sm="5">
              <v-autocomplete
                :items="stores"
                :label="$t('STORE')"
                item-text="name"
                item-value="id"
                hide-details
                clearable
                v-model="filters.storeId"
                @click:clear="clearFilter('storeId')"
              ></v-autocomplete>
            </b-col>
          </template>
        </SearchTools>
        <v-card-title>
          <v-text-field
            v-model="search"
            clearable
            append-icon="mdi-magnify"
            :label="$t('SEARCH')"
            single-line
            hide-details
          ></v-text-field>
        </v-card-title>
        <v-data-table
          :headers="tableHeaders"
          :items="items"
          :search="search"
          :loading="isLoadingStock"
          loading-text="Loading... Please wait"
          :footer-props="{
            showFirstLastPage: true,
            itemsPerPageOptions: rowsPerPageItems,
            firstIcon: 'mdi-arrow-collapse-left',
            lastIcon: 'mdi-arrow-collapse-right'
          }"
          class="elevation-1"
        >
          <template v-slot:item.actions="{ item }">
            <v-btn color="primary" @click="handleOpenItemDialog(item)">
              {{ $t("OPEN_LOTS") }}
            </v-btn>
          </template>
          <template v-slot:item.name="{ item }">
            {{ item.name }}
          </template>
          <template v-slot:item.stockActionItem.unitValue="{ item }">
            <strong>{{ item.stockActionItem.unitValue }}</strong>
            <small> ({{ item.unitShortName }})</small>
          </template>
          <template v-slot:item.forDate="{ item }">
            <router-link
              v-if="checkIfUserHasPermission(['stock-actions-show'])"
              :to="{
                name: 'stock-action-review',
                params: { id: item.stockActionId }
              }"
            >
              {{ item.forDate | formatDate }}
            </router-link>
            <template v-else>{{ item.forDate | formatDate }}</template>
          </template>

          <template v-slot:item.stockActionTypeName="{ item }">
            <v-tooltip left>
              <template v-slot:activator="{ on, attrs }">
                <div color="primary" dark v-bind="attrs" v-on="on">
                  <i :class="getIconOperator(item.stockActionTypeOperator)"></i>

                  <router-link
                    v-if="checkIfUserHasPermission(['stock-actions-show'])"
                    :to="{
                      name: 'stock-action-review',
                      params: { id: item.stockActionId }
                    }"
                  >
                    {{ item.stockActionTypeName }}
                  </router-link>
                  <template v-else>{{ item.stockActionTypeName }}</template>
                </div>
              </template>
              <span>
                <small v-if="_.size(item.scrapping) > 0">
                  {{ item.scrapping.stockScrappingReasonName }}
                  {{ item.scrapping.stockScrappingTypeName }}</small
                >
                <small v-if="_.size(item.testing) > 0">
                  {{ item.testing.stockTestingReasonName }}</small
                >
                <small>
                  {{ item.stockActionItem.docNum }}
                  {{ item.stockActionItem.docDate }}</small
                >
              </span>
            </v-tooltip>
          </template>
          <template v-slot:item.stockActionItem.totalPrice="{ item }">
            <v-tooltip left>
              <template v-slot:activator="{ on, attrs }">
                <strong color="primary" dark v-bind="attrs" v-on="on">{{
                  item.stockActionItem.totalPrice
                }}</strong>
              </template>
              <span>
                <small>
                  ({{ item.stockActionItem.unitValue
                  }}{{ item.unitShortName }} *
                  {{ item.stockActionItem.singlePrice }} +
                  {{ item.stockActionItem.transportTotalPrice }} +
                  {{ item.stockActionItem.customsTotalFee }})</small
                ></span
              >
            </v-tooltip>
          </template>
          <template v-slot:no-results>
            <v-alert :value="true" color="error" icon="warning">
              Your search for "{{ searchQuery }}" found no results.
            </v-alert>
          </template>
        </v-data-table>
      </v-card>

      <v-skeleton-loader
        v-if="isPageLoading"
        :loading="isPageLoading"
        type="table"
      ></v-skeleton-loader>
      <v-overlay :value="!firstLoader && isLoadingSharedStore">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <template v-slot:footer>
        <div class="text-right">
          <v-progress-circular
            v-show="isXmlMzLoading"
            indeterminate
            color="green"
          ></v-progress-circular>
          <v-btn
            v-show="!isXmlMzLoading"
            color="btn-light-success"
            class="btn btn-light-success font-weight-bolder mr-2"
            @click="getMzXML()"
          >
            <i class="far fa-file-excel"></i>
            {{ $t("EXPORT") }}
          </v-btn>
        </div>
      </template>
    </b-card>

    <v-dialog v-model="itemDialog" max-width="900px" :retain-focus="false">
      <StockAuditItemLotsPreview
        :selectedItemLots="selectedItemLots"
        @close="handleItemDialogClose"
      />
    </v-dialog>
  </div>
</template>
<style scoped>
tr.previewTr th {
  font-size: 18px !important;
}
</style>
<script>
//General
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";

import { mapGetters } from "vuex";
import SearchTools from "@/core/components/table/SearchTools.vue";
import FormDatepickerBase from "@/view/content/forms/components/FormDatepickerBase.vue";

import { FETCH_STORES_BASIC } from "@/core/services/store/shared_store.module";
import {
  EXPORT_STOCK_REFERENCE_SUMMARY_XLS,
  FETCH_REFERENCE_SUMMARY
} from "@/modules/stock/store/stock.module";

import { permissionMixin } from "@/core/mixins/permissionMixin.js";

import StockAuditItemLotsPreview from "@/modules/stock/components/audits/StockAuditItemLotsPreview.vue";

import _ from "lodash";
import moment from "moment";

export default {
  name: "StockReferenceSummary",
  mixins: [permissionMixin],
  data() {
    return {
      firstLoader: true,
      isPageLoading: true,
      search: "",
      items: [],
      stores: [],
      date: null,
      allItemsTotalPriceSum: 0,
      searchQuery: "",
      filters: {
        storeId: null,
        date: null
      },
      options: {
        descending: true,
        page: 1,
        itemsPerPage: -1,
        totalItems: 100
      },
      rowsPerPageItems: [-1, 20, 30, 40, 50],
      selectedItemLots: {},
      itemDialog: false,
      columns: {},
      cancelSource: null,
      headers: [
        {
          text: this.$t("ID"),
          value: "id"
        },
        {
          text: this.$t("CODE_MZ"),
          value: "code"
        },
        {
          text: this.$t("ECHO_ID"),
          value: "echoId"
        },
        {
          text: this.$t("NAME_MZ"),
          align: "start",
          filterable: true,
          value: "name",
          sortable: true
        },
        {
          text: this.$t("UNIT_SHORT_NAME"),
          value: "unitShortName",
          sortable: false
        },
        {
          text: this.$t("CURRENT_STOCK"),
          value: "currentStock",
          sortable: false
        },
        {
          text: this.$t("STORE_PRICE"),
          value: "price",
          sortable: false
        },
        {
          text: this.$t("STORE_TOTAL"),
          value: "total",
          sortable: false
        },
        {
          text: this.$t("ACTIONS"),
          value: "actions",
          sortable: false,
          align: "right"
        }
      ]
    };
  },
  components: {
    SearchTools,
    FormDatepickerBase,
    StockAuditItemLotsPreview
  },
  watch: {
    storesParams(value) {
      let vm = this;
    },
    suppliersParams(value) {
      let vm = this;
    }
  },
  mounted() {
    let vm = this;
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Stock Actions", route: { name: "stock-actions-list" } },
      { title: "Stock Actions List" }
    ]);
    vm.getStores();
    vm.getDataFromApi();
  },
  computed: {
    ...mapGetters(["isLoadingStock", "isLoadingSharedStore"]),
    params() {
      return {
        ...this.options,
        query: this.searchQuery,
        storeId: this.filters.storeId,
        forDate: this.filters.date
      };
    },
    storesParams() {
      let storesParams = {};
      return storesParams;
    },
    tableHeaders() {
      let vm = this;

      return vm.headers;
    },
    maxDate() {
      return moment().format("YYYY-MM-DD");
    }
  },
  methods: {
    handleOpenItemDialog(item) {
      let vm = this;
      vm.selectedItemLots = {
        id: item.id,
        name: item.name,
        lots: item.lots,
        currentStock: item.currentStock,
        unitShortName: item.unitShortName
      };
      this.itemDialog = true;
    },
    handleItemDialogClose() {
      this.selectedItemLots = {};
      this.itemDialog = false;
    },
    clearFilter(key) {
      let vm = this;
      vm.$nextTick(function() {
        this.$set(vm.filters, key, null);
      });
    },
    cancelRequest() {
      //Axios cancelSource to stop current search if new value is entered
      if (this.cancelSource) {
        this.cancelSource.cancel("Start new search, stop active search");
      }
    },
    doSearch() {
      let vm = this;
      if (vm.options.page == 1) {
        vm.getDataFromApi();
      } else {
        vm.options.page = 1;
      }
    },
    doClear() {
      let vm = this;
      vm.searchQuery = "";
      vm.filters.storeId = null;
      vm.filters.date = null;
      if (vm.options.page == 1) {
        vm.getDataFromApi();
      } else {
        vm.options.page = 1;
      }
    },
    getMzXML() {
      let vm = this;
      vm.isLoadingStoreDailyTasks = true;
      let params = {
        storeId: vm.filters.storeId,
        date: vm.filters.date
      };
      let apiParams = vm.transformUrlParams(params);
      this.$store
        .dispatch(EXPORT_STOCK_REFERENCE_SUMMARY_XLS, apiParams)
        .then(data => {
          vm.firstLoader = false;
          vm.$nextTick(function() {
            let blob = new Blob([data], {
              type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            let link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = "stock-reference-summary-" + vm.filters.date;
            link.click();
            link.remove();
            vm.dialogMZxml = false;
            if (vm.isPageLoading == true || vm.isXmlMzLoading == true) {
              vm.isPageLoading = false;
              vm.isXmlMzLoading = false;
            }
          });
        })
        .catch(response => {
          console.log(response);
          if (response.status === 404) {
            vm.$notify({
              group: "notify",
              type: "error",
              text: "Not Found"
            });
          }
        });
    },
    getDataFromApi() {
      let vm = this;
      //copy current params to modify
      let params = this.params;
      params.length = params.itemsPerPage; //set how many records to fecth per page
      params.start =
        params.page == 1 ? 0 : params.itemsPerPage * (params.page - 1); //set offset
      let apiParams = vm.transformUrlParams(params);
      // console.log(apiParams);
      vm.$store
        .dispatch(FETCH_REFERENCE_SUMMARY, apiParams)
        .then(data => {
          vm.firstLoader = false;
          vm.$nextTick(function() {
            vm.allItemsTotalPriceSum = 0;
            vm.items = data.data;

            if (vm.isPageLoading == true) {
              vm.isPageLoading = false;
            }
          });
        })
        .catch(response => {
          console.log(response);
          if (response.status === 404) {
            vm.$notify({
              group: "notify",
              type: "error",
              text: "Nout Found"
            });
            this.$router.push({ name: "stock-actions-list" });
          }
        });
    },
    getStores() {
      let vm = this;
      let params = this.storesParams;
      let apiParams = vm.transformUrlParams(params);
      this.$store
        .dispatch(FETCH_STORES_BASIC, apiParams)
        .then(data => {
          this.$set(vm, "stores", data.data);
          vm.filters.storeId = data.data[0].id;
          vm.filters.date = moment().format("YYYY-MM-DD");
        })
        .catch(response => {
          console.log(response);
        });
    },
    transformUrlParams(params) {
      let apiParams =
        "?" +
        Object.keys(params)
          .map(key => key + "=" + params[key])
          .join("&");
      return apiParams;
    }
  }
};
</script>
